import React, { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl, getSocketConfig } from '@easyflow/ui-frontend';
import { useAuth } from '@/contexts/AuthContext';
import { Activity, Wifi, WifiOff, Server, TrendingUp, TrendingDown, ArrowUpDown } from 'lucide-react';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';

interface PortStatus {
  port: number;
  service: string;
  protocol: string;
  status: 'open' | 'closed' | 'filtered' | 'unknown';
  /** Nodi owner che ascoltano effettivamente la porta (es. ['fs1','fs2']). */
  nodes: string[];
  connections: number;
  packets_in: number;
  packets_out: number;
  bytes_in: number;
  bytes_out: number;
}

type PortsStatusData = { ports?: PortStatus[] } | PortStatus[];

interface NetworkStatsSocketPayload {
  ports?: PortStatus[];
  timestamp?: string;
}

interface HistoricalRecord {
  hour?: string;
  timestamp?: string;
  created_at?: string;
  port?: number;
  peak_port?: number;
  service?: string;
  total_connections?: number;
  connections?: number;
  total_bytes_in?: string | number;
  bytes_in?: number;
  total_bytes_out?: string | number;
  bytes_out?: number;
  peak_port_bytes?: string | number;
  status?: string;
}

const PORT_SERVICE_MAP: Record<number, string> = {
  5060: 'SIP Internal',
  5080: 'SIP External',
  7444: 'FreeSWITCH WSS',
  8443: 'WebRTC WSS',
  8021: 'FreeSWITCH ESL',
  443: 'HTTPS',
  80: 'HTTP',
};

interface HistoricalSummary {
  total_records: number;
  period_hours: number;
  total_bytes_in: number;
  total_bytes_out: number;
  avg_connections: number;
  peak_hour: HistoricalRecord | null;
}

export default function NetworkMonitoring() {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [portsStatus, setPortsStatus] = useState<PortStatus[]>([]);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [historicalData, setHistoricalData] = useState<HistoricalRecord[]>([]);
  const [historicalSummary, setHistoricalSummary] = useState<HistoricalSummary | null>(null);
  const [historicalLoading, setHistoricalLoading] = useState(true);

  const MONITORED_PORTS = [
    { port: 5060, service: 'SIP Internal', protocol: 'UDP', description: 'Primary SIP signaling' },
    { port: 5080, service: 'SIP External', protocol: 'UDP', description: 'External SIP trunk' },
    { port: 7444, service: 'FreeSWITCH WSS', protocol: 'TCP', description: 'WebSocket Secure' },
    { port: 8443, service: 'WebRTC WSS', protocol: 'WSS', description: 'WebRTC signaling' },
    { port: 8021, service: 'FreeSWITCH ESL', protocol: 'TCP', description: 'Event Socket Layer' },
  ];

  const { data: portsQueryData } = useQuery({
    queryKey: ['network-ports-status'],
    queryFn: async () => {
      const res = await apiClient.get<PortsStatusData>('/master/network/ports-status');
      if (res?.success && res.data) {
        if (Array.isArray(res.data)) {
          return res.data;
        }
        if (Array.isArray(res.data.ports)) {
          return res.data.ports;
        }
      }
      return [] as PortStatus[];
    },
    staleTime: 10 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: isConnected ? false : 10000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (portsQueryData && portsQueryData.length > 0) {
      setPortsStatus(portsQueryData);
      setLastUpdate(new Date().toLocaleTimeString());
    }
  }, [portsQueryData]);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);

    if (!token) {
      console.warn('No auth token found');
      return;
    }

    let isActive = true;

    // Task #2569 — config Socket.IO condivisa (riconnessione infinita + jitter).
    const socket = io(getSocketUrl(), {
      ...getSocketConfig(),
      auth: { token }
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('subscribe:networkMonitor');
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.on('network:stats', (data: NetworkStatsSocketPayload) => {
      if (!isActive) return;
      if (data.ports && Array.isArray(data.ports)) {
        setPortsStatus(data.ports);
        setLastUpdate(new Date().toLocaleTimeString());
      }
    });

    socket.on('connect_error', () => {
      setIsConnected(false);
    });

    return () => {
      isActive = false;
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe:networkMonitor');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  const { data: historicalQueryData, isError: historicalError } = useQuery({
    queryKey: ['network-historical'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: HistoricalRecord[]; summary: HistoricalSummary }>('/master/network/historical?hours=24');
      // Il backend risponde `{ success, data: { data, summary } }`.
      if (res.success && res.data && Array.isArray(res.data.data)) {
        return { data: res.data.data, summary: res.data.summary ?? null };
      }
      return { data: [] as HistoricalRecord[], summary: null as HistoricalSummary | null };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (historicalQueryData) {
      setHistoricalData(historicalQueryData.data);
      setHistoricalSummary(historicalQueryData.summary);
      setHistoricalLoading(false);
    } else if (historicalError) {
      setHistoricalLoading(false);
    }
  }, [historicalQueryData, historicalError]);

  const formatBytes = (bytes: number | string): string => {
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) || 0 : bytes || 0;
    if (numBytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getPortData = (portNum: number): PortStatus => {
    const found = portsStatus.find(p => p.port === portNum);
    if (found) return found;

    const defaultPort = MONITORED_PORTS.find(p => p.port === portNum);
    return {
      port: portNum,
      service: defaultPort?.service || 'Unknown',
      protocol: defaultPort?.protocol || 'TCP',
      status: 'unknown',
      nodes: [],
      connections: 0,
      packets_in: 0,
      packets_out: 0,
      bytes_in: 0,
      bytes_out: 0,
    };
  };

  const openPorts = portsStatus.filter(p => p.status === 'open').length;
  const totalConnections = portsStatus.reduce((sum, p) => sum + (p.connections || 0), 0);
  const totalBytesIn = portsStatus.reduce((sum, p) => sum + (p.bytes_in || 0), 0);
  const totalBytesOut = portsStatus.reduce((sum, p) => sum + (p.bytes_out || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-500 to-purple-500 bg-clip-text text-transparent">
            Network Port Monitor
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
            Real-time status of VoIP and network ports
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge
            variant="outline"
            className={`px-3 py-1.5 ${
              isConnected 
                ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30' 
                : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30'
            }`}
          >
            <Activity className={`w-3.5 h-3.5 mr-2 ${isConnected ? 'animate-pulse' : ''}`} />
            {isConnected ? 'Live WebSocket' : 'Disconnected'}
          </Badge>
          {lastUpdate && isConnected && (
            <span className="text-xs text-muted-foreground dark:text-muted-foreground/70">
              Last update: {lastUpdate}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-cyan-100 dark:bg-cyan-500/20">
              <Server className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Active Ports</p>
              <p className="text-xl font-bold text-foreground">
                {openPorts}/{MONITORED_PORTS.length}
              </p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-100 dark:bg-purple-500/20">
              <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Connections</p>
              <p className="text-xl font-bold text-foreground">{totalConnections}</p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/20">
              <TrendingDown className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Data In</p>
              <p className="text-xl font-bold text-foreground">{formatBytes(totalBytesIn)}</p>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-amber-100 dark:bg-amber-500/20">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Data Out</p>
              <p className="text-xl font-bold text-foreground">{formatBytes(totalBytesOut)}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {MONITORED_PORTS.map(({ port, description }) => {
          const portData = getPortData(port);
          const isOpen = portData.status === 'open';

          return (
            <GlassCard
              key={port}
              className={`transition-all duration-300 ${
                isOpen 
                  ? 'ring-2 ring-cyan-400/50 dark:ring-cyan-400/30 shadow-lg shadow-cyan-500/10' 
                  : 'opacity-75'
              }`}
            >
              <GlassCardHeader className="pb-2">
                <GlassCardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isOpen ? (
                      <Wifi className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-muted-foreground/70 dark:text-muted-foreground" />
                    )}
                    <span className="font-bold text-foreground text-base">
                      Port {port}
                    </span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className="text-[10px] font-medium bg-muted text-muted-foreground dark:text-muted-foreground/60 border-border dark:border-white/20"
                  >
                    {portData.protocol}
                  </Badge>
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent className="pt-0 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground dark:text-muted-foreground/70">{portData.service}</span>
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] ${
                      isOpen 
                        ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30' 
                        : 'bg-muted dark:bg-muted/500/20 text-muted-foreground dark:text-muted-foreground/70 border-border dark:border-slate-500/30'
                    }`}
                  >
                    {portData.status.toUpperCase()}
                  </Badge>
                </div>

                {portData.nodes.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Server className="w-3 h-3 text-muted-foreground/70" />
                    {portData.nodes.map((node) => (
                      <Badge
                        key={node}
                        variant="outline"
                        className="text-[10px] font-medium uppercase bg-cyan-100 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-500/30"
                      >
                        {node}
                      </Badge>
                    ))}
                  </div>
                )}

                {isOpen && (
                  <div className="pt-2 border-t border-border dark:border-white/10 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-muted-foreground/70">Connections</span>
                      <span className="font-medium text-foreground">{portData.connections}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-muted-foreground/70">In</span>
                      <span className="font-medium text-cyan-600 dark:text-cyan-400">{formatBytes(portData.bytes_in)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-muted-foreground/70">Out</span>
                      <span className="font-medium text-purple-600 dark:text-purple-400">{formatBytes(portData.bytes_out)}</span>
                    </div>
                    <div className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground/70 dark:text-muted-foreground">Packets</span>
                      <span className="text-muted-foreground dark:text-muted-foreground/60">
                        {portData.packets_in.toLocaleString()} / {portData.packets_out.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {!isOpen && (
                  <div className="pt-2 text-center text-xs text-muted-foreground/70 dark:text-muted-foreground">
                    No activity
                  </div>
                )}
              </GlassCardContent>
            </GlassCard>
          );
        })}
      </div>

      <GlassCard>
        <GlassCardContent className="p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg text-foreground">System Status</h3>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground/70 mt-0.5">
                Monitoring {MONITORED_PORTS.length} ports | {openPorts} active
              </p>
            </div>
            {isConnected ? (
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <Activity className="w-5 h-5 animate-pulse" />
                <span className="font-medium text-sm">Receiving real-time updates</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <WifiOff className="w-5 h-5" />
                <span className="font-medium text-sm">Waiting for connection...</span>
              </div>
            )}
          </div>
        </GlassCardContent>
      </GlassCard>

      <GlassCard>
        <GlassCardHeader className="pb-2">
          <GlassCardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5 text-muted-foreground dark:text-muted-foreground/70" />
              <span className="text-foreground">Network Activity - Last 24 Hours</span>
            </div>
            {!historicalLoading && (
              <Badge 
                variant="outline" 
                className="text-xs font-normal bg-muted text-muted-foreground dark:text-muted-foreground/60 border-border dark:border-white/20"
              >
                {historicalData.length} records
              </Badge>
            )}
          </GlassCardTitle>
        </GlassCardHeader>
        <GlassCardContent>
          {historicalSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 rounded-lg bg-muted/50 dark:bg-white/5 border border-border dark:border-white/10">
              <div className="text-center">
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Total Data In</p>
                <p className="text-lg font-semibold text-cyan-600 dark:text-cyan-400">
                  {formatBytes(historicalSummary.total_bytes_in)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Total Data Out</p>
                <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                  {formatBytes(historicalSummary.total_bytes_out)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Avg Connections</p>
                <p className="text-lg font-semibold text-foreground">
                  {historicalSummary.avg_connections}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Period</p>
                <p className="text-lg font-semibold text-foreground">
                  {historicalSummary.period_hours}h
                </p>
              </div>
            </div>
          )}

          {historicalLoading ? (
            <div className="text-center py-12">
              <Activity className="w-8 h-8 mx-auto mb-3 animate-spin text-muted-foreground/70 dark:text-muted-foreground" />
              <p className="text-muted-foreground dark:text-muted-foreground/70">Loading historical data...</p>
            </div>
          ) : historicalData.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-border dark:border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 dark:bg-white/5">
                  <tr className="border-b border-border dark:border-white/10">
                    <th className="text-left p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Time</th>
                    <th className="text-left p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Peak Port</th>
                    <th className="text-left p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Top Service</th>
                    <th className="text-right p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Connections</th>
                    <th className="text-right p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Data In</th>
                    <th className="text-right p-3 font-medium text-muted-foreground dark:text-muted-foreground/60">Data Out</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {historicalData.slice(0, 50).map((record, idx) => (
                    <tr key={idx} className="hover:bg-muted/50 dark:hover:bg-white/5 transition-colors">
                      <td className="p-3 text-muted-foreground dark:text-muted-foreground/70">
                        {new Date(record.hour || record.timestamp || record.created_at || '').toLocaleString()}
                      </td>
                      <td className="p-3 font-medium text-foreground font-mono">
                        {record.peak_port || record.port || '-'}
                      </td>
                      <td className="p-3 text-muted-foreground dark:text-muted-foreground/70">
                        {record.service || 
                         (record.peak_port ? PORT_SERVICE_MAP[record.peak_port] : null) ||
                         (record.port ? PORT_SERVICE_MAP[record.port] : null) || 
                         'Aggregated'}
                      </td>
                      <td className="p-3 text-right text-foreground dark:text-muted-foreground/60">
                        {record.total_connections || record.connections || 0}
                      </td>
                      <td className="p-3 text-right text-cyan-600 dark:text-cyan-400">
                        {formatBytes(record.total_bytes_in || record.bytes_in || 0)}
                      </td>
                      <td className="p-3 text-right text-purple-600 dark:text-purple-400">
                        {formatBytes(record.total_bytes_out || record.bytes_out || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ArrowUpDown className="w-10 h-10 mx-auto mb-3 text-muted-foreground/60 dark:text-muted-foreground" />
              <p className="text-muted-foreground dark:text-muted-foreground/70 font-medium">No historical data available</p>
              <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground mt-1">
                Historical data will appear once network metrics are recorded
              </p>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}
